import { Controller } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '@/shared/components/ui/field';
import { DatePicker, type DatePickerProps } from '@/shared/components/ui/DatePicker';
import { FloatingLabelDatePicker } from './FloatingLabelDatePicker';
import type { FloatingInputVariant } from './FormFieldInput';

interface FormFieldDatePickerProps<T extends FieldValues> extends Omit<
  DatePickerProps,
  'value' | 'onChange'
> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  floatingLabel?: boolean;
  floatingVariant?: FloatingInputVariant;
}

export function FormFieldDatePicker<T extends FieldValues>({
  control,
  name,
  label,
  floatingLabel = false,
  floatingVariant = 'default',
  disabled,
  minDate,
  'data-testid': testId,
  ...props
}: FormFieldDatePickerProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          {!floatingLabel && <FieldLabel htmlFor={name}>{label}</FieldLabel>}

          {floatingLabel ? (
            <FloatingLabelDatePicker
              id={name}
              label={label}
              value={field.value ?? null}
              onChange={(value) => field.onChange(value)}
              invalid={fieldState.invalid}
              disabled={disabled}
              minDate={minDate}
              variant={floatingVariant}
              data-testid={testId}
            />
          ) : (
            <DatePicker
              {...props}
              disabled={disabled}
              minDate={minDate}
              value={field.value ?? null}
              onChange={(value) => field.onChange(value)}
              data-testid={testId}
            />
          )}

          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}
